import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiArrowLeft, FiArrowRight, FiUser } from 'react-icons/fi';
import { FaStar } from 'react-icons/fa';
import './Testimonials.css';

// Données des témoignages
const testimonialsData = [
  {
    id: 1,
    name: "Fatoumata",
    country: "Sénégal",
    text: "Grâce à cette plateforme, j'ai trouvé un logement près de mon université et rencontré d'autres étudiants africains. L'expérience a été incroyable !",
    rating: 5
  },
  {
    id: 2,
    name: "Ahmed",
    country: "Maroc",
    text: "Les événements proposés m'ont permis de développer mon réseau professionnel en France. Je recommande vivement !",
    rating: 5
  },
  {
    id: 3,
    name: "Marie",
    country: "France",
    text: "En tant qu'étudiante française, j'ai pu aider des étudiants internationaux à s'intégrer. Une belle expérience humaine.",
    rating: 4
  },
  {
    id: 4,
    name: "Kwame",
    country: "Ghana",
    text: "Le forum m'a sauvé la vie quand j'avais des problèmes administratifs. La communauté est très solidaire.",
    rating: 5
  },
  {
    id: 5,
    name: "Leïla",
    country: "Tunisie",
    text: "J'ai trouvé un job étudiant en moins d'une semaine grâce aux annonces bien organisées.",
    rating: 4
  },
  {
    id: 6,
    name: "Jean",
    country: "Belgique",
    text: "Super plateforme pour échanger avec des étudiants de différentes cultures. Très enrichissant !",
    rating: 5
  },
  {
    id: 7,
    name: "Aïssata",
    country: "Mali",
    text: "Le système de parrainage m'a beaucoup aidée pour mes débuts à Paris. Merci à toute l'équipe !",
    rating: 5
  },
  {
    id: 8,
    name: "Mehdi",
    country: "Algérie",
    text: "Les ressources pour les démarches administratives sont complètes et à jour. Un vrai gain de temps.",
    rating: 4
  },
  {
    id: 9,
    name: "Binta",
    country: "Guinée",
    text: "J'ai pu trouver des meubles gratuits pour mon studio grâce à la section dons. Super initiative !",
    rating: 5
  },
  {
    id: 10,
    name: "Pierre",
    country: "Canada",
    text: "La communauté est très active et bienveillante. Je me suis fait des amis de partout dans le monde.",
    rating: 4
  }
];

const Testimonials = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const trackRef = useRef(null);
  const cardRef = useRef(null);
  const intervalRef = useRef(null);

  // Détecter si on est sur mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Étoiles de notation
  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <FaStar 
        key={i} 
        className={`star ${i < rating ? 'filled' : ''}`} 
        size={isMobile ? 16 : 20}
      />
    ));
  };

  // Défilement automatique
  useEffect(() => {
    const startAutoScroll = () => {
      intervalRef.current = setInterval(() => {
        setCurrentTestimonial(prev => (prev + 1) % testimonialsData.length);
      }, 5000);
    };

    startAutoScroll();
    return () => clearInterval(intervalRef.current);
  }, []);

  // Animation du carrousel
  useEffect(() => {
    if (trackRef.current && cardRef.current) {
      const cardsPerView = isMobile ? 1 : 3;
      const cardWidth = cardRef.current.offsetWidth + (isMobile ? 20 : 30);
      trackRef.current.style.transition = 'transform 0.5s ease';
      trackRef.current.style.transform = `translateX(-${currentTestimonial * cardWidth}px)`;
      
      // Reset position pour effet infini
      if (currentTestimonial === testimonialsData.length - cardsPerView) {
        setTimeout(() => {
          trackRef.current.style.transition = 'none';
          trackRef.current.style.transform = 'translateX(0)';
          setCurrentTestimonial(0);
        }, 500);
      }
    }
  }, [currentTestimonial, isMobile]);

  const handlePrev = () => {
    clearInterval(intervalRef.current);
    setCurrentTestimonial(prev => 
      prev === 0 ? testimonialsData.length - (isMobile ? 1 : 3) : prev - 1
    );
    setTimeout(() => {
      intervalRef.current = setInterval(() => {
        setCurrentTestimonial(prev => (prev + 1) % testimonialsData.length);
      }, 5000);
    }, 500);
  };

  const handleNext = () => {
    clearInterval(intervalRef.current);
    const cardsPerView = isMobile ? 1 : 3;
    setCurrentTestimonial(prev => (prev + 1) % (testimonialsData.length - cardsPerView + 1));
    setTimeout(() => {
      intervalRef.current = setInterval(() => {
        setCurrentTestimonial(prev => (prev + 1) % testimonialsData.length);
      }, 5000);
    }, 500);
  };

  // Nombre de témoignages visibles selon l'écran
  const visibleTestimonials = testimonialsData.slice(
    0,
    isMobile ? testimonialsData.length : testimonialsData.length
  );

  return (
    <section className="testimonials-section">
      <div className="container">
        <motion.h2 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="testimonials-section-title"
        >
          Ils nous font confiance
        </motion.h2>
        
        <div className="testimonials-wrapper">
          <button 
            className="testimonial-arrow arrow-left" 
            onClick={handlePrev}
            aria-label="Témoignage précédent"
          >
            <FiArrowLeft />
          </button>
          
          <div className="testimonials-carousel">
            <div className="testimonials-track" ref={trackRef}>
              {[...testimonialsData, ...testimonialsData.slice(0, isMobile ? 1 : 3)].map((testimonial, index) => (
                <div 
                  className="testimonial-card" 
                  key={`${testimonial.id}-${index}`}
                  ref={index === 0 ? cardRef : null}
                >
                  <div className="testimonial-avatar">
                    <FiUser />
                  </div>
                  <div className="testimonial-rating">
                    {renderStars(testimonial.rating)}
                  </div>
                  <p className="testimonial-text">
                    {testimonial.text}
                  </p>
                  <p className="testimonial-author">
                    <strong>{testimonial.name}</strong>, {testimonial.country}
                  </p>
                </div>
              ))}
            </div>
          </div>
          
          <button 
            className="testimonial-arrow arrow-right" 
            onClick={handleNext}
            aria-label="Témoignage suivant"
          >
            <FiArrowRight />
          </button>
        </div>
        
        {/* Indicateurs de slide (points) */}
        <div className="testimonial-dots">
          {visibleTestimonials.map((_, index) => (
            <button
              key={index}
              className={`testimonial-dot ${index === currentTestimonial ? 'active' : ''}`}
              onClick={() => {
                clearInterval(intervalRef.current);
                setCurrentTestimonial(index);
                setTimeout(() => {
                  intervalRef.current = setInterval(() => {
                    setCurrentTestimonial(prev => (prev + 1) % testimonialsData.length);
                  }, 5000);
                }, 500);
              }}
              aria-label={`Aller au témoignage ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;